import { useState } from 'react'
import { RohsProductComplianceFileDeclarationOfConformityGenerateDialog } from '@/v2/components/rohs/RohsProductComplianceFileDeclarationOfConformityGenerateDialog'
import { V2ProductDescriptionEditDialog } from '@/v2/components/rohs/V2ProductDescriptionEditDialog'
import { V2RohsApplicabilityStep } from '@/v2/components/rohs-legislation-briefing/V2RohsApplicabilityStep'
import { V2RohsEuDeclarationStep } from '@/v2/components/rohs-legislation-briefing/V2RohsEuDeclarationStep'
import { V2RohsManufacturerInformationStep } from '@/v2/components/rohs-legislation-briefing/V2RohsManufacturerInformationStep'
import { V2RohsPartEvidenceStep } from '@/v2/components/rohs-legislation-briefing/V2RohsPartEvidenceStep'
import { V2RohsProductDescriptionStep } from '@/v2/components/rohs-legislation-briefing/V2RohsProductDescriptionStep'
import { V2RohsProductDrawingsStep } from '@/v2/components/rohs-legislation-briefing/V2RohsProductDrawingsStep'
import { V2RohsProductIdentificationStep } from '@/v2/components/rohs-legislation-briefing/V2RohsProductIdentificationStep'
import { V2RohsProductSignOffStep } from '@/v2/components/rohs-legislation-briefing/V2RohsProductSignOffStep'
import { V2RohsRequiredNextBanner } from '@/v2/components/rohs-legislation-briefing/V2RohsRequiredNextBanner'
import { evaluateRohsProductComplianceFileDeclarationOfConformityRequirements } from '@/v2/lib/rohsProductComplianceFileDeclarationOfConformityRequirements'
import { useTranslation } from 'react-i18next'
import { useGetRohsProductComplianceFiles } from '@/shared/hooks/api/rohsComplianceFiles'
import { useGetProduct } from '@/shared/hooks/api/product'
import {
  useGetProductDrawings,
  isProductDrawingsFilled,
} from '@/shared/hooks/api/rohsProductDrawings'
import {
  useGetProductSharedEvidence,
  isProductSharedEvidenceSlotFilled,
} from '@/shared/hooks/api/productSharedEvidence'
import { useCanWriteOrgData } from '@/shared/hooks/useCanWriteOrgData'
import { BriefingStepList } from '@/shared/briefing'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import {
  buildRohsLegislationBriefing,
  findFirstIncompleteRequiredStep,
} from '../utils/rohsLegislationBriefing'

export function V2RohsLegislationBriefing({
  productId,
  rohsApplies,
}: {
  productId: string
  rohsApplies: boolean
}) {
  const { t } = useTranslation('common')
  const { data: product } = useGetProduct(productId)
  const { data: sharedEvidence } = useGetProductSharedEvidence(productId)
  const { data: productDrawingsEvidence } = useGetProductDrawings(productId, {
    enabled: rohsApplies,
  })
  const {
    data: files,
    isLoading,
    isError,
  } = useGetRohsProductComplianceFiles(productId, {
    enabled: rohsApplies,
    refetchInterval: (query) => {
      const state = query.state.data
      if (state == null) {
        return false
      }
      const hasInFlight = state.outputDocuments.some(
        (document) =>
          document.status === 'PENDING' || document.status === 'PROCESSING'
      )
      return hasInFlight ? 3000 : false
    },
  })
  const canWrite = useCanWriteOrgData()
  const [declarationDialogOpen, setDeclarationDialogOpen] = useState(false)
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false)
  const [declarationProgress, setDeclarationProgress] = useState<string | null>(
    null
  )

  if (!rohsApplies) {
    return null
  }

  if (isLoading) {
    return (
      <div className='space-y-3' aria-busy='true'>
        <Skeleton className='h-20 w-full' />
        <Skeleton className='h-40 w-full' />
      </div>
    )
  }

  if (isError || files == null) {
    return (
      <p className='text-sm text-muted-foreground'>
        {t('v2.pages.products.detail.rohsFiles.loadFailed')}
      </p>
    )
  }

  const hasProductDescription =
    product?.description != null && product.description.trim() !== ''
  const briefing = buildRohsLegislationBriefing({
    hasProductDescription,
    hasProductIdentification: isProductSharedEvidenceSlotFilled(
      sharedEvidence,
      'PRODUCT_IDENTIFICATION'
    ),
    hasManufacturerInformation: isProductSharedEvidenceSlotFilled(
      sharedEvidence,
      'MANUFACTURER_INFORMATION'
    ),
    hasProductDrawings: isProductDrawingsFilled(productDrawingsEvidence),
    files,
  })
  const firstIncompleteRequiredStep = findFirstIncompleteRequiredStep(
    briefing.steps
  )
  const productFile = files.product
  const declarationEvaluation =
    productFile != null
      ? evaluateRohsProductComplianceFileDeclarationOfConformityRequirements({
          productName: product?.name,
          productSku: product?.sku,
          productDescription: product?.description,
          rohsApplies,
          productFile,
          components: files.components,
        })
      : null

  const [
    applicability,
    productDescription,
    productIdentification,
    manufacturerInformation,
    productDrawings,
    partEvidence,
    euDeclaration,
    productSignOff,
  ] = briefing.steps

  let declarationLabel = t(
    'v2.pages.products.detail.rohsBriefing.steps.items.euDeclaration.action'
  )
  if (declarationProgress != null) {
    declarationLabel = declarationProgress
  } else if (euDeclaration.status === 'done') {
    declarationLabel = t(
      'v2.pages.products.detail.rohsBriefing.steps.items.euDeclaration.actionEdit'
    )
  }

  return (
    <div className='space-y-6'>
      <V2RohsRequiredNextBanner
        firstIncompleteStep={firstIncompleteRequiredStep}
      />

      <Alert variant='info'>
        <AlertTitle className='line-clamp-none'>
          {t('v2.pages.products.detail.rohsBriefing.overview.title')}
        </AlertTitle>
        <AlertDescription className='space-y-2'>
          <p>{t('v2.pages.products.detail.rohsBriefing.overview.body')}</p>
        </AlertDescription>
      </Alert>

      <Alert variant='info'>
        <AlertTitle className='line-clamp-none'>
          {t(
            'v2.pages.products.detail.rohsFiles.duties.guidance.nonConformingRegister.title'
          )}
        </AlertTitle>
        <AlertDescription>
          <p>
            {t(
              'v2.pages.products.detail.rohsFiles.duties.guidance.nonConformingRegister.body'
            )}
          </p>
        </AlertDescription>
      </Alert>

      <BriefingStepList
        title={t('v2.pages.products.detail.rohsBriefing.steps.title')}
        description={t('v2.pages.products.detail.rohsBriefing.steps.description')}
      >
        <V2RohsApplicabilityStep step={applicability} index={0} />
        <V2RohsProductDescriptionStep
          step={productDescription}
          index={1}
          description={product?.description}
          hasProductDescription={hasProductDescription}
          canWrite={canWrite}
          onEditDescription={() => {
            setDescriptionDialogOpen(true)
          }}
        />
        <V2RohsProductIdentificationStep
          step={productIdentification}
          index={2}
          productId={productId}
        />
        <V2RohsManufacturerInformationStep
          step={manufacturerInformation}
          index={3}
          productId={productId}
        />
        <V2RohsProductDrawingsStep
          step={productDrawings}
          index={4}
          productId={productId}
        />
        <V2RohsPartEvidenceStep
          step={partEvidence}
          index={5}
          productId={productId}
          rohsApplies={rohsApplies}
        />
        <V2RohsEuDeclarationStep
          step={euDeclaration}
          index={6}
          declarationLabel={declarationLabel}
          declarationEvaluation={declarationEvaluation}
          onOpenDeclarationDialog={() => {
            setDeclarationDialogOpen(true)
          }}
        />
        <V2RohsProductSignOffStep
          step={productSignOff}
          index={7}
          productId={productId}
          productFile={productFile}
        />
      </BriefingStepList>
      <V2ProductDescriptionEditDialog
        productId={productId}
        description={product?.description}
        open={descriptionDialogOpen}
        onOpenChange={setDescriptionDialogOpen}
      />
      {productFile != null ? (
        <RohsProductComplianceFileDeclarationOfConformityGenerateDialog
          open={declarationDialogOpen}
          onOpenChange={setDeclarationDialogOpen}
          productId={productId}
          productName={product?.name}
          productSku={product?.sku}
          productDescription={product?.description}
          rohsApplies={rohsApplies}
          productFile={productFile}
          components={files.components}
          outputDocuments={files.outputDocuments}
          onProgressMessage={setDeclarationProgress}
        />
      ) : null}
    </div>
  )
}
